import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { GradientPicker } from '@hivebrite/design-system/GradientPicker';
import { TextInput } from '@hivebrite/design-system/Inputs';
import { FakeLink } from '@hivebrite/design-system/Link';

import ThemeProvider from 'hb-react/style/ThemeProvider';

export const PICKER_POSITION = Object.freeze({
  TOP_LEFT: 'TOP_LEFT',
  TOP_RIGHT: 'TOP_RIGHT',
  BOTTOM_LEFT: 'BOTTOM_LEFT',
  BOTTOM_RIGHT: 'BOTTOM_RIGHT',
});

const InputGradientPicker = ({
  defaultGradient,
  displayPreview = false,
  gradient,
  onChange,
  pickerPosition = PICKER_POSITION.BOTTOM_LEFT,
  resetText,
  title,
}) => {
  const [displayGradientPicker, setDisplayGradientPicker] = useState(false);

  const [gradientState, setGradientState] = useState(
    gradient || defaultGradient,
  );

  const handleClick = () => {
    setDisplayGradientPicker((prev) => !prev);
  };

  const handleClose = () => {
    setDisplayGradientPicker(false);
  };

  const handleChange = (value) => {
    setGradientState(value);

    if (onChange) {
      onChange(value);
    }
  };

  const Picker = useMemo(
    () => (
      <GradientPicker
        gradient={gradient}
        defaultGradient={defaultGradient}
        onChange={handleChange}
        displayPreview={displayPreview}
        Input={TextInput}
      />
    ),
    [displayGradientPicker],
  );

  const handleReset = () => {
    setGradientState(defaultGradient);
    handleClose();

    if (onChange) {
      onChange(defaultGradient);
    }
  };

  return (
    <ThemeProvider theme="fo">
      <Box>
        <Container>
          <Preview
            $gradient={gradientState.inlineCSS}
            onClick={handleClick}
            title={__('Pick a color')}
          />
          {displayGradientPicker && (
            <Box>
              <Cover onClick={handleClose} />
              <PopoverPositioner $pickerPosition={pickerPosition}>
                {Picker}
              </PopoverPositioner>
            </Box>
          )}
        </Container>
        {(title || resetText) && (
          <WrapperText data-testid="title-gradient-picker">
            {title && <Box>{title}</Box>}
            {resetText && (
              <FakeLink onClick={handleReset}>{resetText}</FakeLink>
            )}
          </WrapperText>
        )}
      </Box>
    </ThemeProvider>
  );
};

InputGradientPicker.propTypes = {
  title: PropTypes.string,
  resetText: PropTypes.string,
  defaultGradient: PropTypes.shape({
    angle: PropTypes.number,
    color1: PropTypes.string,
    color1Percentage: PropTypes.number,
    color2: PropTypes.string,
    color2Percentage: PropTypes.number,
    inlineCSS: PropTypes.string,
    type: PropTypes.oneOf(['linear', 'radial']),
  }).isRequired,
  gradient: PropTypes.shape({
    angle: PropTypes.number,
    color1: PropTypes.string,
    color1Percentage: PropTypes.number,
    color2: PropTypes.string,
    color2Percentage: PropTypes.number,
    inlineCSS: PropTypes.string,
    type: PropTypes.oneOf(['linear', 'radial']),
  }).isRequired,
  pickerPosition: PropTypes.oneOf(Object.keys(PICKER_POSITION)),
  onChange: PropTypes.func,
  displayPreview: PropTypes.bool,
};

export default InputGradientPicker;

const Container = styled(Box).attrs({
  display: 'inline-block',
  position: 'relative',
})``;

const Preview = styled(Box).attrs(() => ({
  width: 30,
  height: 30,
  cursor: 'pointer',
}))`
  border: 1px ${({ theme }) => theme.colors.dove} solid;
  border-radius: 6px;
  background-image: ${({ $gradient }) => $gradient};
`;

const Cover = styled(Box)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const WrapperText = styled(Box).attrs({
  pl: 3,
  display: 'inline-block',
  verticalAlign: 'middle',
})``;

const PopoverPositioner = styled(Box).attrs({
  position: 'absolute',
})`
  z-index: 120;

  ${({ $pickerPosition }) => {
    switch ($pickerPosition) {
      case PICKER_POSITION.BOTTOM_LEFT:
        return css`
          left: 0;
          margin-top: 4px;
          top: 100%;
        `;
      case PICKER_POSITION.BOTTOM_RIGHT:
        return css`
          right: 0;
          margin-top: 4px;
          top: 100%;
        `;
      case PICKER_POSITION.TOP_RIGHT:
        return css`
          right: 0;
          margin-bottom: 6px;
          bottom: 100%;
        `;
      case PICKER_POSITION.TOP_LEFT:
      default:
        return css`
          left: 0;
          margin-bottom: 6px;
          bottom: 100%;
        `;
    }
  }}
`;
